import { useEffect, useMemo } from 'react';

import { ApolloClient, ErrorLike, gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { useQuery as useTanstackQuery } from '@tanstack/react-query';
import isEmpty from 'lodash-es/isEmpty';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { conversationsUtilsEndpoints } from '@/api/endpoints';
import { callPost } from '@/api/fetcher';
import { selectFilters } from '@/modules/analytics/redux/selectors';
import { selectAccountId, selectBrainId } from '@/redux/session/selectors';
import { trackEvent } from '@/segment/segment';
import { Log, LogSessionContentRow, LogsQueryVariables } from '@/shared/types/analytics';
import { EventName } from '@/shared/types/segment';
import { getAnalyticsRangeLocal } from '@/util/analytics';
import { computeSessionBoundDate } from '@/util/dateUtils';
import { getGraphQLSet, parseFilter, isUuid } from '@/util/util';

import {
  clearPrefetchedSessionContent,
  getPrefetchedSessionContent,
} from './sessionContentPrefetchCache';

type QueryVariables = { excludeStartTime?: string } & LogsQueryVariables;

export interface LogSessionsPreview {
  rows: Log[];
}

export type LogSessionContent = { rows: LogSessionContentRow[] };

export interface LogSessionsCount {
  rows: { total_count: number }[];
}

export interface LogsHook {
  isLoading?: boolean;
  previewError?: ErrorLike;
  contentError?: ErrorLike;
  numberOfData?: number;
  refetchPreview?: () => void;
  refetchContent?: () => void;
  loadMore?: () => Promise<ApolloClient.QueryResult<LogSessionsPreview>> | null;
  data?: Log[];
  content?: LogSessionContentRow;
  isTranslationError?: boolean;
}

export const SESSIONS_PREVIEW = gql`
  query ListSessions(
    $accountId: uuid
    $agentIds: _text
    $brainIds: _uuid
    $channels: _text
    $channelUserId: String
    $deskIds: _uuid
    $endDate: timestamp
    $externalUserId: String
    $hasCollection: Boolean
    $hasRecording: Boolean
    $hasVoicemail: Boolean
    $integrationIds: _uuid
    $isContained: Boolean
    $isCovered: Boolean
    $isMeaningful: Boolean
    $isTest: Boolean
    $isProactive: Boolean
    $languageModelIds: _text
    $minNumUserMessages: Int
    $ratings: _int4
    $sessionId: String
    $startDate: timestamp
    $tags: _text
    $tagsMatch: String
    $userEmail: String
    $userId: String
    $userName: String
    $limit: Int
    $offset: Int
    $min_num_thumbs_up: Int
    $min_num_thumbs_down: Int
    $csatBrain: _int4
    $goalAchieved: _int4
    $userSentiment: _int4
    $insightFilters: jsonb
  ) {
    rows: list_sessions(
      args: {
        account_id: $accountId
        agent_ids: $agentIds
        brain_parent_ids: $brainIds
        channels: $channels
        channel_user_id: $channelUserId
        csat_brain: $csatBrain
        goal_achieved: $goalAchieved
        user_sentiment: $userSentiment
        desk_ids: $deskIds
        end_time: $endDate
        external_user_id: $externalUserId
        has_collection: $hasCollection
        has_recording: $hasRecording
        has_voicemail: $hasVoicemail
        integration_ids: $integrationIds
        is_contained: $isContained
        is_covered: $isCovered
        is_meaningful: $isMeaningful
        is_test: $isTest
        is_proactive: $isProactive
        language_model_ids: $languageModelIds
        min_num_user_messages: $minNumUserMessages
        ratings: $ratings
        session_id: $sessionId
        start_time: $startDate
        tags: $tags
        tags_match: $tagsMatch
        user_email: $userEmail
        user_id: $userId
        user_name: $userName
        min_num_thumbs_up: $min_num_thumbs_up
        min_num_thumbs_down: $min_num_thumbs_down
        insight_filters: $insightFilters
        row_limit: $limit
        row_offset: $offset
      }
    ) {
      channel
      desk_id
      end_time
      is_contained
      is_covered
      is_test
      participated_agents
      participated_brains
      participated_language_models
      rating
      session_id
      start_time
      tags
      thumbs_down
      thumbs_up
      total_user_messages
      user_id
      user_name
      csat_brain
      csat_human_agent
      goal_achieved
      user_sentiment
      conversation_summary
      insights
    }
  }
`;

const SESSION_CONTENT_FIELDS = `
  brain_id
  brain_parent_id
  brain_version
  channel
  channel_user_id
  conversation_summary
  csat_brain
  csat_human_agent
  desk_id
  end_time
  expired_time
  external_user_id
  feedback
  goal_achieved
  insights
  integration_id
  is_contained
  is_covered
  is_test
  messages
  participated_agents
  participated_brains
  participated_collections
  rating
  rule_ids
  session_id
  start_time
  tags
  total_user_messages
  user_email
  user_id
  user_name
  user_sentiment
`;

export const SESSION_CONTENT = gql`
  query GetSessionContent($sessionId: String) {
    rows: get_session_content(args: { session_id: $sessionId }) {
      ${SESSION_CONTENT_FIELDS}
    }
  }
`;

export const SESSION_CONTENT_BOUNDED = gql`
  query GetSessionContentBounded($sessionId: String, $startDate: timestamp) {
    rows: get_session_content(
      args: { session_id: $sessionId, start_time: $startDate }
    ) {
      ${SESSION_CONTENT_FIELDS}
    }
  }
`;

export const SESSIONS_COUNT = gql`
  query ListSessionsCount(
    $accountId: uuid
    $agentIds: _text
    $brainIds: _uuid
    $channels: _text
    $channelUserId: String
    $deskIds: _uuid
    $endDate: timestamp
    $externalUserId: String
    $hasCollection: Boolean
    $hasRecording: Boolean
    $hasVoicemail: Boolean
    $integrationIds: _uuid
    $isContained: Boolean
    $isCovered: Boolean
    $isMeaningful: Boolean
    $isTest: Boolean
    $isProactive: Boolean
    $languageModelIds: _text
    $minNumUserMessages: Int
    $ratings: _int4
    $sessionId: String
    $startDate: timestamp
    $tags: _text
    $tagsMatch: String
    $userEmail: String
    $userId: String
    $userName: String
    $min_num_thumbs_up: Int
    $min_num_thumbs_down: Int
    $csatBrain: _int4
    $goalAchieved: _int4
    $userSentiment: _int4
    $insightFilters: jsonb
  ) {
    rows: list_sessions_count(
      args: {
        account_id: $accountId
        agent_ids: $agentIds
        brain_parent_ids: $brainIds
        channels: $channels
        channel_user_id: $channelUserId
        desk_ids: $deskIds
        end_time: $endDate
        external_user_id: $externalUserId
        has_collection: $hasCollection
        has_recording: $hasRecording
        has_voicemail: $hasVoicemail
        integration_ids: $integrationIds
        is_contained: $isContained
        is_covered: $isCovered
        is_meaningful: $isMeaningful
        is_test: $isTest
        is_proactive: $isProactive
        language_model_ids: $languageModelIds
        min_num_user_messages: $minNumUserMessages
        ratings: $ratings
        session_id: $sessionId
        start_time: $startDate
        tags: $tags
        tags_match: $tagsMatch
        user_email: $userEmail
        user_id: $userId
        user_name: $userName
        min_num_thumbs_up: $min_num_thumbs_up
        min_num_thumbs_down: $min_num_thumbs_down
        csat_brain: $csatBrain
        goal_achieved: $goalAchieved
        user_sentiment: $userSentiment
        insight_filters: $insightFilters
      }
    ) {
      total_count
    }
  }
`;

interface UseLogsProps {
  limit?: number;
  sessionId?: string;
  sessionStart?: string;
  user_id?: string;
  language?: string;
}

const useLogs = ({
  limit = 25,
  sessionId,
  sessionStart,
  user_id = null,
  language,
}: UseLogsProps = {}): LogsHook => {
  const { t } = useTranslation();
  const accountId = useSelector(selectAccountId);
  const brainId = useSelector(selectBrainId);

  const { startDate, endDate, logs, filtersLoaded } = useSelector(selectFilters);

  const variables = Object.assign(
    {},
    {
      accountId,
      brainIds: getGraphQLSet([brainId]),
      ...getAnalyticsRangeLocal(startDate, endDate),
    },
    ...logs.map((filter) => {
      if (filter.type == 'reactions') {
        return {
          min_num_thumbs_up: filter[filter.type] === true ? 1 : 0,
          min_num_thumbs_down: filter[filter.type] === false ? 1 : 0,
        };
      }
      if (filter.type === 'tags') {
        return { tags: parseFilter(filter), tagsMatch: filter.tagsMode ?? 'any' };
      }
      return { [filter.type]: parseFilter(filter) };
    })
  );
  const { data: numberOfData } = useQuery<LogSessionsCount, QueryVariables>(SESSIONS_COUNT, {
    variables: { ...variables },
    skip: !!sessionId || !accountId || !brainId || !filtersLoaded,
    // Count is non-critical — loadMore still guards with length checks
    errorPolicy: 'ignore',
  });

  const {
    loading: previewLoading,
    data: previewData,
    error: previewError,
    fetchMore,
    refetch: refetchPreview,
  } = useQuery<LogSessionsPreview, QueryVariables>(SESSIONS_PREVIEW, {
    variables: { ...variables, limit, offset: 0 },
    skip: !!sessionId || !!user_id || !brainId || !accountId || !filtersLoaded,
  });

  const computedStartDate = useMemo(
    () => (sessionStart ? computeSessionBoundDate(sessionStart) : undefined),
    [sessionStart]
  );

  const {
    loading: contentLoading,
    data: contentData,
    error: contentError,
    refetch: refetchContent,
  } = useQuery<LogSessionContent>(computedStartDate ? SESSION_CONTENT_BOUNDED : SESSION_CONTENT, {
    variables: computedStartDate ? { sessionId, startDate: computedStartDate } : { sessionId },
    skip: !sessionId || !accountId || !isUuid(sessionId),
  });

  const finalData = previewData?.rows ?? [];

  const loadMore = () => {
    if (
      startDate &&
      // Prevent extra call with empty data
      finalData.length < (numberOfData?.rows?.[0]?.total_count ?? 0)
    ) {
      return fetchMore({
        query: SESSIONS_PREVIEW,
        variables: { ...variables, limit, offset: finalData.length },
        updateQuery: (prev, { fetchMoreResult }) => {
          if (!isEmpty(prev)) {
            const finalData = [...prev.rows, ...fetchMoreResult.rows];
            return { ...prev, rows: finalData };
          }
          return fetchMoreResult;
        },
      });
    }
    return null;
  };
  const shouldTranslate =
    Boolean(contentData?.rows?.[0]?.messages) &&
    Boolean(language) &&
    !contentLoading &&
    isUuid(sessionId);

  const { status: translationStatus } = useTanstackQuery({
    queryKey: ['translatedLogs', sessionId, language],
    queryFn: () => callPost(conversationsUtilsEndpoints.translateLogs(sessionId, language), {}),
    enabled: shouldTranslate,
  });

  useEffect(() => {
    if (translationStatus === 'success') {
      toast.success(t('try_it.logs_translated_toast'));
      trackEvent(EventName.ConversationTranslateSuccess, { session_id: sessionId, language });
    } else if (translationStatus === 'error') {
      toast.error(t('try_it.logs_translated_error_toast'));
      trackEvent(EventName.ConversationTranslateError, { session_id: sessionId, language });
    }
  }, [translationStatus, t, sessionId, language]);

  // Fallback for when useQuery doesn't pick up Apollo's cache. Read per render, not once on
  // mount, since the replay modal swaps `sessionId` in place without remounting.
  const prefetchedContent = sessionId ? getPrefetchedSessionContent(sessionId) : undefined;

  const queriedContent = contentData?.rows?.[0] as LogSessionContentRow | undefined;
  const candidateContent = queriedContent ?? prefetchedContent;

  // Discard a row whose session_id doesn't match: consumers key attachment paths on it.
  const sessionContent =
    sessionId && candidateContent?.session_id && candidateContent.session_id !== sessionId
      ? undefined
      : candidateContent;

  // Clean up the prefetch cache entry when the hook unmounts
  useEffect(() => {
    return () => {
      if (sessionId) {
        clearPrefetchedSessionContent(sessionId);
      }
    };
  }, [sessionId]);

  if ((previewLoading || previewError) && !sessionContent) {
    return { isLoading: previewLoading, previewError, refetchPreview };
  }

  if ((contentLoading && !sessionContent) || contentError) {
    return { isLoading: contentLoading, contentError, refetchContent };
  }

  return {
    isLoading: false,
    data: finalData,
    numberOfData: numberOfData?.rows?.[0]?.total_count ?? 0,
    loadMore,
    content: sessionContent,
    isTranslationError: translationStatus === 'error',
  };
};

export default useLogs;
